import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  CalendarOutlined,
  CheckOutlined,
  CloseOutlined
} from "@ant-design/icons";
import { useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { Alert, Button, InputNumber, Spin, Tooltip, message } from "antd";
import {
  fetchSprHolidays,
  setSprHoliday,
  unsetSprHoliday
} from "./spr-holidays-api";
import SprHolidaysFillModal from "./spr-holidays-fill-modal";
import SprHolidaysMonth from "./spr-holidays-month";
import { buildHolidayMap, getLawFlag } from "./spr-holidays-utils";
import "./spr-holidays.css";

const currentYear = () => new Date().getFullYear();

const yearFromSearchParams = (params) => {
  const src =
    params instanceof URLSearchParams ? params : new URLSearchParams(params || "");
  if (!src.has("year")) return currentYear();
  const y = Number(src.get("year"));
  return Number.isFinite(y) && y >= 1900 && y <= 2100 ? y : currentYear();
};

/**
 * Справочник праздников — годовой календарь (Delphi TFrmSprHolidays).
 */
const SprHolidays = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const year = useMemo(() => yearFromSearchParams(searchParams), [searchParams]);

  const law = useSelector((state) => state.user.session?.law);
  const canEdit = getLawFlag(law, 39) === 1;

  const [holidayMap, setHolidayMap] = useState(() => new Map());
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [selectedKey, setSelectedKey] = useState(null);
  const [fillOpen, setFillOpen] = useState(false);

  const listReqRef = useRef(0);

  const loadList = useCallback(() => {
    const requestId = ++listReqRef.current;
    setLoading(true);
    setError("");
    return fetchSprHolidays(year)
      .then((data) => {
        if (requestId !== listReqRef.current) return;
        setHolidayMap(buildHolidayMap(data));
      })
      .catch((e) => {
        if (requestId !== listReqRef.current) return;
        setError(e.message || "Не удалось загрузить праздники");
        setHolidayMap(new Map());
      })
      .finally(() => {
        if (requestId === listReqRef.current) setLoading(false);
      });
  }, [year]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  useEffect(() => {
    const next = new URLSearchParams();
    next.set("year", String(year));
    if (searchParams.toString() === next.toString()) return;
    setSearchParams(next, { replace: true });
  }, [year, searchParams, setSearchParams]);

  useEffect(() => {
    setSelectedKey((prev) => {
      if (!prev) return null;
      return prev.startsWith(`${year}-`) ? prev : null;
    });
  }, [year]);

  const selectedInDb = useMemo(
    () => !!selectedKey && holidayMap.has(selectedKey),
    [selectedKey, holidayMap]
  );

  const setYear = (value) => {
    if (value == null) return;
    const y = Number(value);
    if (!Number.isFinite(y) || y < 1900 || y > 2100) return;
    const next = new URLSearchParams(searchParams);
    next.set("year", String(y));
    setSearchParams(next, { replace: true });
  };

  const applySet = async (dateKey) => {
    if (!canEdit || !dateKey || busy) return;
    setBusy(true);
    try {
      await setSprHoliday(dateKey);
      setHolidayMap((prev) => {
        const next = new Map(prev);
        next.set(dateKey, { vid: 0 });
        return next;
      });
    } catch (e) {
      message.error(e.message || "Не удалось отметить выходной");
      await loadList();
    } finally {
      setBusy(false);
    }
  };

  const applyUnset = async (dateKey) => {
    if (!canEdit || !dateKey || busy) return;
    if (!holidayMap.has(dateKey)) return;
    setBusy(true);
    try {
      await unsetSprHoliday(dateKey);
      setHolidayMap((prev) => {
        const next = new Map(prev);
        next.delete(dateKey);
        return next;
      });
    } catch (e) {
      message.error(e.message || "Не удалось снять отметку");
      await loadList();
    } finally {
      setBusy(false);
    }
  };

  const onToggle = async (dateKey) => {
    if (!canEdit || !dateKey) return;
    setSelectedKey(dateKey);
    if (holidayMap.has(dateKey)) {
      await applyUnset(dateKey);
    } else {
      await applySet(dateKey);
    }
  };

  const onSetClick = () => {
    if (!selectedKey) {
      message.info("Выберите день");
      return;
    }
    applySet(selectedKey);
  };

  const onUnsetClick = () => {
    if (!selectedKey) {
      message.info("Выберите день");
      return;
    }
    applyUnset(selectedKey);
  };

  return (
    <div className="spr-holidays">
      <div className="spr-holidays__toolbar">
        <div className="spr-holidays__extra-actions">
          <Tooltip title="Выходной">
            <span>
              <Button
                icon={<CheckOutlined />}
                disabled={!canEdit || !selectedKey || busy}
                aria-label="Выходной"
                onClick={onSetClick}
              />
            </span>
          </Tooltip>
          <Tooltip title="Рабочий">
            <span>
              <Button
                icon={<CloseOutlined />}
                disabled={!canEdit || !selectedKey || !selectedInDb || busy}
                aria-label="Рабочий"
                onClick={onUnsetClick}
              />
            </span>
          </Tooltip>
          <Tooltip title="Заполнить">
            <span>
              <Button
                icon={<CalendarOutlined />}
                disabled={!canEdit || busy}
                aria-label="Заполнить"
                onClick={() => setFillOpen(true)}
              />
            </span>
          </Tooltip>
        </div>
        <label className="spr-holidays__year">
          <span>Год</span>
          <InputNumber
            min={1900}
            max={2100}
            value={year}
            onChange={setYear}
          />
        </label>
      </div>

      {error ? (
        <Alert className="spr-holidays__error" type="error" showIcon message={error} />
      ) : null}

      <div className="spr-holidays__body">
        {loading ? (
          <div className="spr-holidays__center">
            <Spin />
          </div>
        ) : (
          <>
            <div className="spr-holidays__grid">
              {Array.from({ length: 12 }, (_, monthIndex) => (
                <SprHolidaysMonth
                  key={monthIndex}
                  year={year}
                  monthIndex={monthIndex}
                  holidayMap={holidayMap}
                  selectedKey={selectedKey}
                  onSelect={setSelectedKey}
                  onToggle={onToggle}
                />
              ))}
            </div>
            <div className="spr-holidays__legend" aria-label="Легенда">
              <span className="spr-holidays__legend-item">
                <span className="spr-holidays__legend-swatch spr-holidays__legend-swatch--weekend" />
                Выходные дни
              </span>
              <span className="spr-holidays__legend-item">
                <span className="spr-holidays__legend-swatch spr-holidays__legend-swatch--fest" />
                Праздничные дни
              </span>
            </div>
          </>
        )}
      </div>

      <SprHolidaysFillModal
        open={fillOpen}
        year={year}
        onClose={() => setFillOpen(false)}
        onFilled={loadList}
      />
    </div>
  );
};

export default SprHolidays;
